/**
 * Dark-panel "community profile" card shown by ProfileTooltipService when
 * the player hovers (or clicks, in the runner) a procedural author surface.
 *
 *   ┌──────────────────────────────────────────────────┐ ▪
 *   │ [32x32]  @leafstomp42                            │
 *   │ avatar   "Speedrunner turned shopkeeper."        │
 *   │          12 levels · 87 trades                   │
 *   │          #towers #speedrun                       │
 *   │                            Member since 03.2024  │
 *   └──────────────────────────────────────────────────┘
 *
 * Layout sized for the 384×216 NATIVE canvas — 140 px wide × 90 px tall.
 * Tagline is auto-wrapped to two lines if needed.
 *
 * Pure render component — owns no input handlers; the surrounding scene
 * drives visibility, position, and the profile binding.
 */

import Phaser from "phaser";

import { UGC_CYAN } from "../config";
import type { AuthorProfile } from "../types";

import type { AuthorIdentityService } from "../services/AuthorIdentityService";

export const PROFILE_CARD_WIDTH = 140;
export const PROFILE_CARD_HEIGHT = 90;
const AVATAR_SIZE = 32;
const PADDING = 4;
const NAME_X = AVATAR_SIZE + PADDING * 2;
const NAME_Y = 8;
const TAGLINE_X = PADDING;
const TAGLINE_Y = AVATAR_SIZE + PADDING * 2;
const STATS_Y = TAGLINE_Y + 22;
const TAGS_Y = STATS_Y + 10;
const MEMBER_SINCE_Y = PROFILE_CARD_HEIGHT - PADDING - 8;

const BG_COLOR = 0x1c1c24;
const BG_ALPHA = 0.94;
const TEXT_LIGHT = 0xcfd8dc;
const TEXT_MID = 0x9ea7b3;
const TEXT_DIM = 0x7d8696;

export class ProfileTooltipCard {
  readonly container: Phaser.GameObjects.Container;

  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly username: Phaser.GameObjects.Text;
  private readonly tagline: Phaser.GameObjects.Text;
  private readonly stats: Phaser.GameObjects.Text;
  private readonly tags: Phaser.GameObjects.Text;
  private readonly memberSince: Phaser.GameObjects.Text;
  private avatar?: Phaser.GameObjects.Image;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly identityService: AuthorIdentityService,
  ) {
    this.container = scene.add.container(0, 0);

    this.bg = scene.add
      .rectangle(0, 0, PROFILE_CARD_WIDTH, PROFILE_CARD_HEIGHT, BG_COLOR, BG_ALPHA)
      .setOrigin(0, 0)
      .setStrokeStyle(1, UGC_CYAN);

    const cornerMark = scene.add
      .rectangle(PROFILE_CARD_WIDTH - 4, 0, 3, 3, UGC_CYAN, 1)
      .setOrigin(0, 0);

    this.username = scene.add
      .text(NAME_X, NAME_Y, "", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(0, 0);

    this.tagline = scene.add
      .text(TAGLINE_X, TAGLINE_Y, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(TEXT_LIGHT),
        wordWrap: { width: PROFILE_CARD_WIDTH - PADDING * 2, useAdvancedWrap: true },
      })
      .setOrigin(0, 0);

    this.stats = scene.add
      .text(TAGLINE_X, STATS_Y, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(TEXT_MID),
      })
      .setOrigin(0, 0);

    this.tags = scene.add
      .text(TAGLINE_X, TAGS_Y, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(TEXT_LIGHT),
      })
      .setOrigin(0, 0);

    this.memberSince = scene.add
      .text(PROFILE_CARD_WIDTH - PADDING, MEMBER_SINCE_Y, "", {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(TEXT_DIM),
      })
      .setOrigin(1, 0);

    this.container.add([this.bg, cornerMark, this.username, this.tagline, this.stats, this.tags, this.memberSince]);
    this.container.setVisible(false);
  }

  setProfile(profile: AuthorProfile, strings: { stats: string; memberSince: string }): void {
    if (this.avatar) {
      this.avatar.destroy();
      this.avatar = undefined;
    }
    this.avatar = this.identityService.drawCircleAvatar(
      this.scene,
      PADDING,
      PADDING,
      profile,
      AVATAR_SIZE,
    );
    this.container.add(this.avatar);

    this.username.setText(`@${profile.username}`);
    this.tagline.setText(profile.tagline);
    this.stats.setText(strings.stats);
    this.tags.setText(profile.tags.map((t) => `#${t}`).join("  "));
    this.memberSince.setText(strings.memberSince);
  }

  /** Top-left position of the card inside the scene's coordinate space. */
  setPosition(x: number, y: number): void {
    this.container.setPosition(Math.round(x), Math.round(y));
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  setDepth(depth: number): void {
    this.container.setDepth(depth);
  }

  destroy(): void {
    this.container.destroy();
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}
